// @flow

import getPinTitle from 'app/common/react/components/PinRep/utils/getPinTitle';
import getPinTypes from 'app/common/react/components/PinRep/utils/getPinTypes';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type SourceLinkStyle } from 'app/common/react/components/PinRep/components/ActionBar/SourceLink';
import { type Surface } from 'app/common/react/components/PinRep/core/PinRepConfiguration';
import {
  type FeedbackData,
  type FeedbackType,
} from 'app/common/react/components/PinRep/feedback-types';

export const hiddenOverflowMenuSurfaces = [
  'BrioCloseupRelatedProducts',
  'EngagementDetails',
  'UserProfilePinGrid',
];

type BuildActionBarPropsArgs = {|
  actionBarEditBeforeShare: boolean,

  pin: Pin,

  feedbackData: FeedbackData,

  // Indicates why a pin appears in a given grid
  feedbackType: FeedbackType,

  // What grid is showing this PinRep?
  surface: Surface,

  // Descriptive props handed down from PinRepConfiguration
  inChangeTitlesExp: boolean,
  isDenseView: boolean,
  isGroupBoard: boolean,
  isOwnBoard: boolean,
|};

export type BuildActionBarPropsOutput = {|
  actionBarEditBeforeShare: boolean,
  feedbackData: FeedbackData,
  feedbackType: FeedbackType,
  pinTitle: string,
  showEditButton: boolean,
  showOverflowMenu: boolean,
  showShareButton: boolean,
  sourceLinkStyle: SourceLinkStyle,
  useDenseGroupBoardLayout: boolean,
|};

// This function takes _descriptive_ props and outputs _imperative_ props describing the PinRep ActionBar.
// It does not handle what specific pin data values we show. The ActionBar can handle that.
export default function buildActionBarProps({
  actionBarEditBeforeShare,
  feedbackData,
  feedbackType,
  inChangeTitlesExp,
  isDenseView,
  isGroupBoard,
  isOwnBoard,
  pin,
  surface,
}: BuildActionBarPropsArgs) {
  const { isOwnPin, isPromoted } = getPinTypes(pin);

  const pinTitle = getPinTitle({ inChangeTitlesExp, isGroupBoard, pin, surface });

  const showEditButton = isGroupBoard && isOwnPin;

  const showOverflowMenu = !(
    isPromoted ||
    isOwnBoard ||
    isGroupBoard ||
    hiddenOverflowMenuSurfaces.includes(surface)
  );

  const showShareButton = !['BrioCloseupRelatedProducts', 'CloseupRelatedProducts'].includes(
    surface,
  );

  const useDenseGroupBoardLayout = isGroupBoard && isDenseView;

  let sourceLinkStyle = 'default';
  if (isDenseView) {
    sourceLinkStyle = isGroupBoard ? 'icon-only' : 'truncated-text';
  }

  return {
    actionBarEditBeforeShare,
    feedbackData,
    feedbackType,
    pinTitle,
    showEditButton,
    showOverflowMenu,
    showShareButton,
    sourceLinkStyle,
    useDenseGroupBoardLayout,
  };
}
